import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { validateDocumentFile, PDFPreview } from "../utils/pdfConversion";
import useCVAnalysis from "../hooks/useCVAnalysis";
import * as mammoth from 'mammoth'; // For DOCX conversion
import { useTokens } from "../hooks/useTokens";
import { FiTrash2 } from "react-icons/fi";

// Helper function to get category tag class
const getCategoryClass = (category) => {
  if (!category) return '';
  
  // Normalize the category name for consistent CSS class handling
  // Remove spaces, slashes, convert to lowercase
  let normalizedCategory = category.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/\//g, '-')
    .replace(/[^a-z0-9-]/g, '');
  
  // Handle special cases based on your data
  if (normalizedCategory.includes('medical-education')) return 'medical-education';
  if (normalizedCategory.includes('medical-content-writer')) return 'medical-content-writer';
  if (normalizedCategory.includes('telemedicine')) return 'telemedicine';
  if (normalizedCategory.includes('cruise-ship-doctor')) return 'cruise-ship-doctor';
  if (normalizedCategory.includes('business-consulting')) return 'business-consulting';
  if (normalizedCategory.includes('medical-officer')) return 'medical-officer';
  if (normalizedCategory.includes('research-medical-officer')) return 'research-medical-officer';
  if (normalizedCategory.includes('registrar')) return 'registrar';
    
  // Default fallback - return the first part of the category
  return normalizedCategory.split('-')[0];
};

const jobCategories = [
  { id: "medical-education", name: "Medical Education" , isActive: true},
  { id: "business-consulting", name: "Business Consulting" , isActive: true},
  { id: "medical-officer", name: "Medical Officer" , isActive: true},
  { id: "medical-content-writer", name: "Medical Content Writer" , isActive: false},
  { id: "telemedicine", name: "Telemedicine" , isActive: false},
  { id: "cruise-ship-doctor", name: "Cruise Ship Doctor" , isActive: false},
  // { id: "research-medical-officer", name: "Research Medical Officer" , isActive: false},
  // { id: "registrar", name: "Registrar" , isActive: false},
];

const countries = [
  { id: "South Africa", name: "South Africa" },
  { id: "United Kingdom", name: "United Kingdom" },
  { id: "United States", name: "United States" },
  // { id: "Canada", name: "Canada" },
  // { id: "Australia", name: "Australia" },
  // { id: "New Zealand", name: "New Zealand" },
  // { id: "Germany", name: "Germany" },
  // { id: "Netherlands", name: "Netherlands" },
];

const CVAnalyserPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("South Africa");
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [confirmPII, setConfirmPII] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  
  const countryDropdownRef = useRef(null);
  
  // Token management
  const { tokens,  addTokens, deductTokens } = useTokens();
  
  const { 
    loading, 
    error, 
    statusMessage, 
    submitCV
  } = useCVAnalysis(user);

  const requiredTokens = 250; // Each CV analysis requires 250 tokens

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    const category = jobCategories.find(cat => cat.id === categoryId);
    if (category && category.isActive) {
      setSelectedCategory(categoryId);
    }
  };

  // Handle country selection
  const handleCountrySelect = (countryId) => {
    setSelectedCountry(countryId);
  };

  // Handle file selection through input
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const fileObj = e.target.files[0];
      const validation = validateDocumentFile(fileObj);
      
      if (validation.valid) {
        setFile(fileObj);
        setValidationError(null);
        
        if (fileObj.type === 'application/pdf') {
          setPreviewVisible(true);
        } else {
          setPreviewVisible(false);
        }
      } else {
        setValidationError(validation.message);
        setPreviewVisible(false);
      }
    }
  };

  const handleDeleteFile = (e) => {
    e.preventDefault();
    setFile(null);
    setValidationError(null);
    setPreviewVisible(false);
    setDragActive(false);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const fileObj = e.dataTransfer.files[0];
      const validation = validateDocumentFile(fileObj);
      
      if (validation.valid) {
        setFile(fileObj);
        setValidationError(null);
        
        if (fileObj.type === 'application/pdf') {
          setPreviewVisible(true);
        } else {
          setPreviewVisible(false);
        }
      } else {
        setValidationError(validation.message);
        setPreviewVisible(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCategory) {
      setValidationError("Please select a job category");
      return;
    }

    if (!selectedCountry) {
      setValidationError("Please select a country");
      return;
    }
    
    if (!file) {
      setValidationError("Please upload a CV/resume file");
      return;
    }

    // Check if user has sufficient tokens
    if (tokens < requiredTokens) {
      alert("You do not have enough tokens to analyze your CV. Please purchase more tokens.");
      return;
    }
    
    try {
      await deductTokens(requiredTokens);
      
      const result = await submitCV({
        file,
        jobCategory: selectedCategory,
        country: selectedCountry,
        dependencies: {
          mammoth // Pass mammoth as a dependency for DOCX extraction
        }
      });
      
      if (result && result.success) {
        navigate(`/cv-analysis-results?id=${result.requestId}`);
      } else {
        await addTokens(requiredTokens);
        setValidationError("CV analysis failed. Token has been refunded.");
      }
    } catch (tokenError) {
      console.error('Token deduction error:', tokenError);
      setValidationError("Insufficient tokens or token deduction failed.");
    }
  };

  const togglePreview = () => {
    if (file && file.type === 'application/pdf') {
      setPreviewVisible(!previewVisible);
    }
  };

  const displayError = validationError || error;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="bg-indigo-50 p-4 rounded-lg mb-6">
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
            </svg>
            <p className="text-indigo-700 font-medium">Your first CV Analyser credit is on us. Spot gaps, fix issues, and stand out.</p>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Let us help you shape a CV that reflects your full potential
        </h1>
        
        <p className="text-lg text-gray-700 mb-8">
          Upload your CV for the role you're applying to, and we'll give you tailored feedback to help you improve it.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Step 1: Pick the Job category you are applying for</h2>
          <div className="flex flex-wrap gap-3">
            {jobCategories.map((category) => {
              const categoryClass = getCategoryClass(category.id);
              const isDisabled = !category.isActive;
              return (
                <button
                  key={category.id}
                  type="button"
                  disabled={isDisabled}
                  className={`py-2 px-4 rounded-full text-sm font-medium transition-colors category-pill ${categoryClass} ${
                    selectedCategory === category.id ? `${categoryClass} active` : ''
                  } ${
                    isDisabled 
                      ? 'opacity-50 !cursor-not-allowed !bg-gray-200 !text-gray-500 hover:bg-gray-200' 
                      : 'hover:bg-opacity-80'
                  }`}
                  onClick={() => !isDisabled && handleCategorySelect(category.id)}
                >
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Step 2: Select the country you are applying for</h2>
          <div className="w-full relative" ref={countryDropdownRef}>
            <button
              type="button"
              onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCountryDropdownOpen(!countryDropdownOpen);
                }
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-left flex justify-between items-center"
              aria-haspopup="listbox"
              aria-expanded={countryDropdownOpen}
            >
              <span>{selectedCountry}</span>
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {countryDropdownOpen && (
              <div 
                className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                role="listbox"
              >
                {countries.map((country) => (
                  <button
                    key={country.id}
                    type="button"
                    className={`w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                      selectedCountry === country.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      handleCountrySelect(country.id);
                      setCountryDropdownOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCountrySelect(country.id);
                        setCountryDropdownOpen(false);
                      }
                    }}
                    role="option"
                    aria-selected={selectedCountry === country.id}
                  >
                    {country.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Step 3: Upload your CV</h2>
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center ${
              dragActive ? "border-indigo-500 bg-indigo-50" : "border-gray-300"
            }`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            <div className="mb-4 flex justify-center">
              <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-medium mb-2">Choose a file to drag and drop here.</h3>
            <p className="text-sm text-gray-500 mb-4">PDF, Docx or Doc, up to 50mb.</p>
            
            {file ? (
              <div className="mt-4 text-left p-3 bg-gray-50 rounded-md">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                  {file.type === 'application/pdf' && (
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={togglePreview}
                        className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                      >
                        {previewVisible ? 'Hide preview' : 'Show preview'}
                      </button>

                      <button
                        type="button"
                        onClick={(e) => handleDeleteFile(e)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete file"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <label className="inline-flex items-center justify-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer">
                  Browse files
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* PDF Preview */}
        {file && file.type === 'application/pdf' && previewVisible && (
          <div className="mt-6 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium">Document Preview</h3>
              <button
                type="button"
                onClick={() => setPreviewVisible(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <PDFPreview file={file} maxPages={3} />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              We'll extract the text from your PDF for analysis. The preview helps ensure the document is correctly formatted.
            </p>
          </div>
        )}

        {displayError && (
          <div className="mt-4 text-red-600 text-sm">{displayError}</div>
        )}

        {statusMessage && (
          <div className="mt-4">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="rounded-full bg-indigo-400 h-3 w-3"></div>
              <div className="h-4 bg-indigo-200 rounded w-full max-w-md"></div>
            </div>
            <p className="text-sm text-indigo-700 mt-2">{statusMessage}</p>
          </div>
        )}

        <div className="mt-2 text-center">

          <div className="flex items-center space-x-4 mb-5">
            <input
              type="checkbox"
              checked={confirmPII}
              onChange={() => setConfirmPII(!confirmPII)}
              id="confirm-pii"
              className="w-6 h-6 border-2 border-gray-300 bg-white appearance-none checked:border-[#061B16] relative"
              style={{ position: "relative" }}
            />

            <label htmlFor="confirm-pii" className="text-lg text-gray-900 leading-snug text-left flex-1 relative">
            I understand that I should remove any personally identifiable information (emails, phone numbers, addresses, etc.) 
            from my CV before uploading to protect my privacy. While we do not store your CV, 
            removing this sensitive data ensures it is never sent to external services.
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !file || !selectedCategory || !selectedCountry || !confirmPII}
            className={`px-8 py-3 rounded-md text-white font-medium ${
              loading || !file || !selectedCategory || !selectedCountry || !confirmPII
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-black hover:bg-gray-800"
            }`}
          >
            {loading ? "Processing..." : "Next"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CVAnalyserPage;